import { 
  CognitoUserPool, 
  CognitoUser, 
  AuthenticationDetails,
  CognitoUserSession 
} from 'amazon-cognito-identity-js';
import CONFIG from '../config';

export interface User {
  email: string;
  sub: string;
}

class AuthService {
  private userPool: CognitoUserPool;

  constructor() {
    this.userPool = new CognitoUserPool({
      UserPoolId: CONFIG.COGNITO.USER_POOL_ID,
      ClientId: CONFIG.COGNITO.CLIENT_ID
    });
  }

  signIn(email: string, password: string): Promise<User> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result: CognitoUserSession) => {
          // JWTトークンをローカルストレージに保存
          localStorage.setItem('accessToken', result.getAccessToken().getJwtToken());
          localStorage.setItem('idToken', result.getIdToken().getJwtToken());
          
          const user: User = {
            email: email,
            sub: result.getIdToken().payload.sub
          };
          
          resolve(user);
        },
        onFailure: (err) => {
          reject(new Error(err.message || 'ログインに失敗しました'));
        }
      });
    });
  }

  signUp(email: string, password: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userPool.signUp(email, password, [], null as any, (err, result) => {
        if (err) {
          reject(new Error(err.message || 'サインアップに失敗しました'));
        } else {
          resolve(result);
        }
      });
    });
  }

  confirmSignUp(email: string, code: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(new Error(err.message || '確認に失敗しました'));
        } else {
          resolve(result);
        }
      });
    });
  }

  signOut(): void {
    const currentUser = this.userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('accessToken');
    return token !== null;
  }

  getCurrentUser(): User | null {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) return null;
    
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return {
        email: payload.email,
        sub: payload.sub
      };
    } catch (error) {
      console.error('Token parse error:', error);
      return null;
    }
  }
}

const authService = new AuthService();

export const signIn = (email: string, password: string) => authService.signIn(email, password);
export const signUp = (email: string, password: string) => authService.signUp(email, password);
export const confirmSignUp = (email: string, code: string) => authService.confirmSignUp(email, code);
export const signOut = () => authService.signOut();
export const isAuthenticated = () => authService.isAuthenticated();
export const getCurrentUser = () => authService.getCurrentUser();